import { BitfStrapiModel } from '@common/libs/bitforce/core/models';
import { Convention } from './convention.model';
import { IAttachment } from '@common/interfaces/attachments.interface';
import { eAttachmentFileName } from '@web/enums/attachnents-file-name.enum';
import { EContractStatus } from '@web/enums/contract-status.enum';
import { Invoice } from './invoice.model';
import { WorkOrder } from './work-order.model';
import { Revenue } from './revenue.model';

export class Contract extends BitfStrapiModel {
  businessName: string;
  cig: string;
  contractActivationDate: string;
  contractAmount: number;
  contractDurationInMonths: number;
  convention: Convention;
  conventionId: string;
  dec: string;
  documentId: string;
  attachments: IAttachment[];
  invoices: Invoice[];
  contractId: string;
  contractName: string;
  orderId: string;
  publishedAt: Date;
  contractUpdatedAt: Date;
  entryDate: Date;
  res: string;
  tags: string;
  nso: string;
  revenues: Revenue[];
  seller: string;
  state: EContractStatus;
  workOrders: WorkOrder[];
  contractParent: Contract;
  isInvoiced: boolean;
  isPsAssigned: boolean;
  isAllAttachmentSigned: boolean;
  isContracted: boolean;
  expirationDate: Date;

  constructor(data: Partial<Contract>) {
    super({ data });

    if (data.publishedAt) {
      this.publishedAt = new Date(data.publishedAt);
    }

    if (data.contractUpdatedAt) {
      this.contractUpdatedAt = new Date(data.contractUpdatedAt);
    }

    if (data.entryDate) {
      this.entryDate = new Date(data.entryDate);
    }

    if (data.expirationDate) {
      this.expirationDate = new Date(data.expirationDate);
    }

    if (data.invoices) {
      this.invoices = data.invoices.map(invoice => new Invoice(invoice));
    }

    if (data.workOrders) {
      this.workOrders = data.workOrders.map(workOrder => new WorkOrder(workOrder));
    }

    if (data.revenues) {
      this.revenues = data.revenues.map(revenue => new Revenue(revenue));
    }

    if (data.convention) {
      this.convention = new Convention(data.convention);
    }

    if (data.contractParent) {
      this.contractParent = new Contract(data.contractParent);
    }

    if (data.workOrders) {
      this.workOrders = data.workOrders.map(workOrder => new WorkOrder(workOrder));
    }

    if (data.attachments) {
      eAttachmentFileName.forEach(name => {
        const existingAttachment = this.attachments.find(
          attachment => attachment.name.toLowerCase() === name
        );

        if (!existingAttachment) {
          this.attachments.push({
            id: null,
            name: name.charAt(0).toUpperCase() + name.slice(1),
            attachmentStatus: null,
          });
        }
      });
    }
  }

  get serialised() {
    return {
      state: this.state,
      attachments: this.attachments.map(attachment => {
        delete attachment?.id;
        return attachment;
      }),
      cig: this.cig,
      tags: this.tags,
      nso: this.nso,
      invoices: this.invoices?.map(invoice => invoice.documentId),
      convention: this.convention?.id || null,
      isInvoiced: this.isInvoiced,
      isPsAssigned: this.isPsAssigned,
      isAllAttachmentSigned: this.isAllAttachmentSigned,
      isContracted: this.isContracted,
    };
  }

  get tagsArray() {
    return this.tags ? this.tags.split(',') : [];
  }

  get nsoCodesArray() {
    return this.nso ? this.nso.split(',') : [];
  }

  get contractEndDate(): Date | null {
    // Se abbiamo expirationDate dal backend, usalo
    if (this.expirationDate) {
      return this.expirationDate;
    }

    // Altrimenti calcola dal contractActivationDate e contractDurationInMonths
    if (!this.contractActivationDate || !this.contractDurationInMonths) {
      return null;
    }

    const startDate = new Date(this.contractActivationDate);
    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + this.contractDurationInMonths);

    return endDate;
  }

  get daysUntilExpiration(): number | null {
    const endDate = this.contractEndDate;
    if (!endDate) {
      return null;
    }

    const today = new Date();
    const timeDiff = endDate.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    return daysDiff;
  }

  get showExpirationWarning(): boolean {
    const daysRemaining = this.daysUntilExpiration;
    return daysRemaining !== null && daysRemaining <= 60 && daysRemaining > 0;
  }

  get showExpirationEndWarning(): boolean {
    const daysRemaining = this.daysUntilExpiration;
    return daysRemaining !== null && daysRemaining <= 0 && !this.isInvoiced;
  }
}
